import { focusToSourceRect, IImageFocus } from "@eventr/common";
import { create } from "zustand";
import { setStatusBarStyleDark, setStatusBarStyleLight } from "./statusBar";

/** How light the thing behind the status bar is; the bar's text takes the opposite. */
export type TStatusBarTone = "light" | "dark";

/**
 * WCAG relative luminance above which black text out-contrasts white. It is the point where
 * the two contrast ratios cross, so each side of it picks whichever text reads better.
 */
const LUMINANCE_SPLIT = 0.179;

const channel = (c: number) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

const luminance = (r: number, g: number, b: number) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

const toneOfLuminance = (l: number): TStatusBarTone => (l > LUMINANCE_SPLIT ? "light" : "dark");

/** Any CSS colour as rgb, read back off a 1px canvas so theme colours in any notation work. */
function rgbOf(colour: string): [number, number, number] | undefined {
  const ctx = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
  if (!ctx) return undefined;
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b];
}

export function toneOfColour(colour: string): TStatusBarTone {
  const rgb = rgbOf(colour);
  return rgb ? toneOfLuminance(luminance(...rgb)) : "dark";
}

interface IImageStrip {
  /** The picture as painted, a data url or an app asset. */
  url: string;
  focus?: IImageFocus;
  /** The box the picture covers, in px. */
  frameWidth: number;
  frameHeight: number;
  /** How much of the top of that box the status bar sits over, in px. */
  stripHeight: number;
  /** The colour washed over the picture there, and how strongly. */
  wash?: { colour: string; alpha: number };
}

/** Enough samples to average a strip, few enough to be free on every slide. */
const SAMPLE_WIDTH = 24;
const SAMPLE_HEIGHT = 4;

/**
 * The tone of the part of a cover-framed picture that sits under the status bar. It crops
 * the source the way the renderer does (focus, zoom and all), so a picture that is dark
 * overall but has a pale sky across the top reads as light.
 */
export function toneOfImageStrip({ url, focus, frameWidth, frameHeight, stripHeight, wash }: IImageStrip) {
  return new Promise<TStatusBarTone | undefined>((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(undefined);
    img.onload = () => {
      const ctx = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
      if (!ctx || !img.naturalWidth || !img.naturalHeight || frameHeight <= 0) return resolve(undefined);
      ctx.canvas.width = SAMPLE_WIDTH;
      ctx.canvas.height = SAMPLE_HEIGHT;

      const shown = focusToSourceRect(focus, img.naturalWidth / img.naturalHeight, frameWidth / frameHeight);
      const stripShare = Math.min(stripHeight / frameHeight, 1);
      ctx.drawImage(
        img,
        shown.left * img.naturalWidth,
        shown.top * img.naturalHeight,
        shown.width * img.naturalWidth,
        shown.height * stripShare * img.naturalHeight,
        0,
        0,
        SAMPLE_WIDTH,
        SAMPLE_HEIGHT,
      );
      if (wash) {
        ctx.globalAlpha = wash.alpha;
        ctx.fillStyle = wash.colour;
        ctx.fillRect(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
      }

      // Averaged in luminance, not in rgb: white text has to read over the bright patches
      // and the dark ones alike, and averaging colours first flattens both into grey.
      const { data } = ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
      let total = 0;
      for (let i = 0; i < data.length; i += 4) total += luminance(data[i]!, data[i + 1]!, data[i + 2]!);
      resolve(toneOfLuminance(total / (data.length / 4)));
    };
    img.src = url;
  });
}

interface IStatusBarToneState {
  /** Each itinerary's cover picture under the status bar, by itinerary id, once sampled. */
  imageTones: Record<string, TStatusBarTone>;
  /** Whether the Home header is what the status bar sits over, rather than the page. */
  headerUnderBar: boolean;
  setImageTone: (id: string, tone: TStatusBarTone) => void;
  setHeaderUnderBar: (under: boolean) => void;
}

export const useStatusBarTone = create<IStatusBarToneState>((set) => ({
  imageTones: {},
  headerUnderBar: true,
  setImageTone: (id, tone) => set((s) => (s.imageTones[id] === tone ? s : { imageTones: { ...s.imageTones, [id]: tone } })),
  setHeaderUnderBar: (under) => set((s) => (s.headerUnderBar === under ? s : { headerUnderBar: under })),
}));

/** Light text over a dark background and dark text over a light one. */
export const applyStatusBarTone = (tone: TStatusBarTone) =>
  tone === "light" ? setStatusBarStyleLight() : setStatusBarStyleDark();
